import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import moment from 'moment/moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
  getFieldNamesFromColumnConfig,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { useAllOrgUnits } from '~/data/orgunits';
import { BirthdaysReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import { convertToDateOrUndefined } from '~/utils/helpers/date';

export type BirthdaysReportDataRow = {
  employeeId: number;
  dateOfBirth: Date | undefined;
  firstName: string;
  lastName: string;
  unitName: string;
  managerName: string;
  daysToNextBirthday: number;
  currentAge: number;
};

type PropsType = {
  data: BirthdaysReportData[];
};

export const useBirthdaysReport = ({ data }: PropsType) => {
  const { t } = useTranslation();

  const [nextDays, setNextDays] = useState(-1);

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const { getEmployeeById } = useAllEmployees();
  const { orgUnits } = useAllOrgUnits();

  const rows: BirthdaysReportDataRow[] = useMemo(() => {
    const getManagerName = (managerId: number) => {
      const manager = getEmployeeById(managerId);
      return manager ? `${manager.fEmpFirstName} ${manager.fEmpLastName}` : '';
    };

    const getDaysToNextBirthday = (dob: string): number => {
      const nextBirthday = moment(convertToDateOrUndefined(dob)).set(
        'year',
        moment().year(),
      );

      if (nextBirthday.isBefore(moment())) {
        nextBirthday.add(1, 'year');
      }

      return nextBirthday.diff(moment(), 'days');
    };

    const getOrgUnitName = (unitId: number): string => {
      const orgUnit = orgUnits?.find((ou) => ou.fOrgUnitId === unitId);
      return orgUnit ? orgUnit.fOrgUnitName : '';
    };

    return data
      .map((birthdayDetail) => {
        const employeeDetails = getEmployeeById(birthdayDetail.fEmpId);

        const dateOfBirth = convertToDateOrUndefined(birthdayDetail.fEmpDob);

        return {
          employeeId: birthdayDetail.fEmpId,
          dateOfBirth,
          firstName: employeeDetails?.fEmpFirstName || '',
          lastName: employeeDetails?.fEmpLastName || '',
          unitName: getOrgUnitName(employeeDetails?.fEmpUnit || -1),
          managerName: getManagerName(employeeDetails?.fEmpManagerId || -1),
          daysToNextBirthday: getDaysToNextBirthday(birthdayDetail.fEmpDob),
          currentAge: moment().diff(dateOfBirth, 'years'),
        };
      })
      .filter(({ daysToNextBirthday }) => {
        if (nextDays === -1) {
          return true;
        }
        return daysToNextBirthday <= nextDays;
      });
  }, [data, getEmployeeById, nextDays, orgUnits]);

  const { columnsConfigs, columnGroupingModel } = useMemo(() => {
    const employeeColumnConfigs: GridColDef<BirthdaysReportDataRow>[] = [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 30,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'unitName',
        headerName: t('laOrgUnit'),
        minWidth: 180,
      },
      {
        field: 'managerName',
        headerName: t('laManager'),
        minWidth: 180,
      },
    ];

    const birthdayColumnConfigs: GridColDef<BirthdaysReportDataRow>[] = [
      {
        field: 'dateOfBirth',
        headerName: t('laDoB'),
        minWidth: 150,
        type: 'date',
        valueFormatter: dateCellValueFormatter,
      },
      {
        field: 'daysToNextBirthday',
        headerName: t('laDaysToBirth'),
        minWidth: 150,
      },
      {
        field: 'currentAge',
        headerName: t('laAge'),
        minWidth: 150,
      },
    ];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumnConfigs),
      },
      {
        groupId: 'birthday',
        headerName: t('laBirthdays'),
        children: getFieldNamesFromColumnConfig(birthdayColumnConfigs),
      },
    ];

    return {
      columnsConfigs: [...employeeColumnConfigs, ...birthdayColumnConfigs],
      columnGroupingModel,
    };
  }, [t, goToEmployeeBasicDataView]);

  return {
    table: {
      rows,
      columnsConfigs,
      columnGroupingModel,
    },
    nextDays,
    onNextDaysFilterChange: (value: number) => {
      setNextDays(value);
    },
  };
};
